import { invariant } from "../../../utils/invariant.js";
import { $isLinkNode } from "../LinkPlugin/nodes/LinkNode/LinkNode.js";
import { $createAutoLinkNode, $isAutoLinkNode, AutoLinkNode } from "../LinkPlugin/nodes/AutoLinkNode/AutoLinkNode.js";
import { c } from "react-compiler-runtime";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $createTextNode, $getSelection, $isElementNode, $isLineBreakNode, $isNodeSelection, $isRangeSelection, $isTextNode, TextNode } from "lexical";
import { useEffect } from "react";
import { mergeRegister } from "@lexical/utils";

//#region src/plugins/Links/AutoLinkPlugin/AutoLinkPlugin.ts
const URL_MATCHER = /((https?:\/\/(www\.)?)|(www\.))[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)/;
const EMAIL_MATCHER = /(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))/;
const MATCHERS = [(text) => {
	const match = URL_MATCHER.exec(text);
	if (match === null) return null;
	const fullMatch = match[0];
	return {
		index: match.index,
		length: fullMatch.length,
		text: fullMatch,
		url: fullMatch.startsWith("http") ? fullMatch : `https://${fullMatch}`
	};
}, (text) => {
	const match = EMAIL_MATCHER.exec(text);
	return match && {
		index: match.index,
		length: match[0].length,
		text: match[0],
		url: `mailto:${match[0]}`
	};
}];
function findFirstMatch(text, matchers) {
	for (let i = 0; i < matchers.length; i++) {
		const match = matchers[i](text);
		if (match) return match;
	}
	return null;
}
const PUNCTUATION_OR_SPACE = /[.,;\s]/;
function isSeparator(char) {
	return PUNCTUATION_OR_SPACE.test(char);
}
function endsWithSeparator(textContent) {
	return isSeparator(textContent[textContent.length - 1]);
}
function startsWithSeparator(textContent) {
	return isSeparator(textContent[0]);
}
function isPreviousNodeValid(node) {
	let previousNode = node.getPreviousSibling();
	if ($isElementNode(previousNode)) previousNode = previousNode.getLastDescendant();
	return previousNode === null || $isLineBreakNode(previousNode) || $isTextNode(previousNode) && endsWithSeparator(previousNode.getTextContent());
}
function isNextNodeValid(node) {
	let nextNode = node.getNextSibling();
	if ($isElementNode(nextNode)) nextNode = nextNode.getFirstDescendant();
	return nextNode === null || $isLineBreakNode(nextNode) || $isTextNode(nextNode) && startsWithSeparator(nextNode.getTextContent());
}
function isContentAroundIsValid(matchStart, matchEnd, text, nodes) {
	const contentBeforeIsValid = matchStart > 0 ? isSeparator(text[matchStart - 1]) : isPreviousNodeValid(nodes[0]);
	if (!contentBeforeIsValid) return false;
	const contentAfterIsValid = matchEnd < text.length ? isSeparator(text[matchEnd]) : isNextNodeValid(nodes[nodes.length - 1]);
	return contentAfterIsValid;
}
function extractMatchingNodes(nodes, startIndex, endIndex) {
	const unmodifiedBeforeNodes = [];
	const matchingNodes = [];
	const unmodifiedAfterNodes = [];
	let matchingOffset = 0;
	let currentOffset = 0;
	const currentNodes = [...nodes];
	while (currentNodes.length > 0) {
		const currentNode = currentNodes[0];
		const currentNodeText = currentNode.getTextContent();
		const currentNodeLength = currentNodeText.length;
		const currentNodeStart = currentOffset;
		const currentNodeEnd = currentOffset + currentNodeLength;
		if (currentNodeEnd <= startIndex) {
			unmodifiedBeforeNodes.push(currentNode);
			matchingOffset += currentNodeLength;
		} else if (currentNodeStart >= endIndex) unmodifiedAfterNodes.push(currentNode);
		else matchingNodes.push(currentNode);
		currentOffset += currentNodeLength;
		currentNodes.shift();
	}
	return [
		matchingOffset,
		unmodifiedBeforeNodes,
		matchingNodes,
		unmodifiedAfterNodes
	];
}
function createAutoLinkNode(nodes, startIndex, endIndex, match) {
	const linkNode = $createAutoLinkNode(match.url, match.attributes);
	if (nodes.length === 1) {
		let remainingTextNode = nodes[0];
		let linkTextNode;
		if (startIndex === 0) [linkTextNode, remainingTextNode] = remainingTextNode.splitText(endIndex);
		else [, linkTextNode, remainingTextNode] = remainingTextNode.splitText(startIndex, endIndex);
		const textNode = $createTextNode(match.text);
		textNode.setFormat(linkTextNode.getFormat());
		textNode.setDetail(linkTextNode.getDetail());
		linkNode.append(textNode);
		linkTextNode.replace(linkNode);
		return remainingTextNode;
	}
	if (nodes.length > 1) {
		const firstTextNode = nodes[0];
		let offset = firstTextNode.getTextContent().length;
		let firstLinkTextNode;
		if (startIndex === 0) firstLinkTextNode = firstTextNode;
		else [, firstLinkTextNode] = firstTextNode.splitText(startIndex);
		const linkNodes = [];
		let remainingTextNode;
		for (let i = 1; i < nodes.length; i++) {
			const currentNode = nodes[i];
			const currentNodeText = currentNode.getTextContent();
			const currentNodeLength = currentNodeText.length;
			const currentNodeStart = offset;
			const currentNodeEnd = offset + currentNodeLength;
			if (currentNodeStart < endIndex) if (currentNodeEnd <= endIndex) linkNodes.push(currentNode);
			else {
				const [linkTextNode, endNode] = currentNode.splitText(endIndex - currentNodeStart);
				linkNodes.push(linkTextNode);
				remainingTextNode = endNode;
			}
			offset += currentNodeLength;
		}
		const selection = $getSelection();
		const selectedTextNode = selection ? selection.getNodes().find($isTextNode) : void 0;
		const textNode = $createTextNode(firstLinkTextNode.getTextContent());
		textNode.setFormat(firstLinkTextNode.getFormat());
		textNode.setDetail(firstLinkTextNode.getDetail());
		linkNode.append(textNode, ...linkNodes);
		if (selectedTextNode && selectedTextNode === firstLinkTextNode) {
			if ($isRangeSelection(selection)) textNode.select(selection.anchor.offset, selection.focus.offset);
			else if ($isNodeSelection(selection)) textNode.select(0, textNode.getTextContent().length);
		}
		firstLinkTextNode.replace(linkNode);
		return remainingTextNode;
	}
	return void 0;
}
function handleLinkCreation(nodes, matchers, onChange) {
	let currentNodes = [...nodes];
	let text = currentNodes.map((node) => node.getTextContent()).join("");
	let match;
	let invalidMatchEnd = 0;
	while ((match = findFirstMatch(text, matchers)) && match !== null) {
		const matchStart = match.index;
		const matchLength = match.length;
		const matchEnd = matchStart + matchLength;
		const isValid = isContentAroundIsValid(invalidMatchEnd + matchStart, invalidMatchEnd + matchEnd, text, currentNodes);
		if (isValid) {
			const [matchingOffset, , matchingNodes, unmodifiedAfterNodes] = extractMatchingNodes(currentNodes, invalidMatchEnd + matchStart, invalidMatchEnd + matchEnd);
			const actualMatchStart = invalidMatchEnd + matchStart - matchingOffset;
			const actualMatchEnd = invalidMatchEnd + matchEnd - matchingOffset;
			const remainingTextNode = createAutoLinkNode(matchingNodes, actualMatchStart, actualMatchEnd, match);
			currentNodes = remainingTextNode ? [remainingTextNode, ...unmodifiedAfterNodes] : unmodifiedAfterNodes;
			onChange(match.url, null);
			invalidMatchEnd = 0;
		} else invalidMatchEnd += matchEnd;
		text = text.substring(matchEnd);
	}
}
function handleLinkEdit(linkNode, matchers, onChange) {
	const children = linkNode.getChildren();
	const childrenLength = children.length;
	for (let i = 0; i < childrenLength; i++) {
		const child = children[i];
		if (!$isTextNode(child) || !child.isSimpleText()) {
			replaceWithChildren(linkNode);
			onChange(null, linkNode.getURL());
			return;
		}
	}
	const text = linkNode.getTextContent();
	const match = findFirstMatch(text, matchers);
	if (match === null || match.text !== text) {
		replaceWithChildren(linkNode);
		onChange(null, linkNode.getURL());
		return;
	}
	if (!isPreviousNodeValid(linkNode) || !isNextNodeValid(linkNode)) {
		replaceWithChildren(linkNode);
		onChange(null, linkNode.getURL());
		return;
	}
	const url = linkNode.getURL();
	if (url !== match.url) {
		linkNode.setURL(match.url);
		onChange(match.url, url);
	}
	if (match.attributes) {
		const rel = linkNode.getRel();
		if (rel !== match.attributes.rel) {
			linkNode.setRel(match.attributes.rel || null);
			onChange(match.attributes.rel || null, rel);
		}
		const target = linkNode.getTarget();
		if (target !== match.attributes.target) {
			linkNode.setTarget(match.attributes.target || null);
			onChange(match.attributes.target || null, target);
		}
	}
}
function handleBadNeighbors(textNode, matchers, onChange) {
	const previousSibling = textNode.getPreviousSibling();
	const nextSibling = textNode.getNextSibling();
	const text = textNode.getTextContent();
	if ($isAutoLinkNode(previousSibling) && !startsWithSeparator(text)) {
		previousSibling.append(textNode);
		handleLinkEdit(previousSibling, matchers, onChange);
		onChange(null, previousSibling.getURL());
	}
	if ($isAutoLinkNode(nextSibling) && !endsWithSeparator(text)) {
		replaceWithChildren(nextSibling);
		handleLinkEdit(nextSibling, matchers, onChange);
		onChange(null, nextSibling.getURL());
	}
}
function replaceWithChildren(node) {
	const children = node.getChildren();
	const childrenLength = children.length;
	for (let j = childrenLength - 1; j >= 0; j--) node.insertAfter(children[j]);
	node.remove();
	return children.map((child) => child.getLatest());
}
function getTextNodesToMatch(textNode) {
	const textNodesToMatch = [textNode];
	let nextSibling = textNode.getNextSibling();
	while (nextSibling !== null && $isTextNode(nextSibling) && nextSibling.isSimpleText()) {
		textNodesToMatch.push(nextSibling);
		if (/[\s]/.test(nextSibling.getTextContent())) break;
		nextSibling = nextSibling.getNextSibling();
	}
	return textNodesToMatch;
}
function useAutoLink(editor, matchers, onChange) {
	const $ = c(5);
	let t0;
	let t1;
	if ($[0] !== editor || $[1] !== matchers || $[2] !== onChange) {
		t0 = () => {
			if (!editor.hasNodes([AutoLinkNode])) invariant(false, "LexicalAutoLinkPlugin: AutoLinkNode not registered on editor");
			const onChangeWrapped = (url, prevUrl) => {
				if (onChange) onChange(url, prevUrl);
			};
			return mergeRegister(editor.registerNodeTransform(TextNode, (textNode) => {
				const parent = textNode.getParentOrThrow();
				const previous = textNode.getPreviousSibling();
				if ($isAutoLinkNode(parent)) handleLinkEdit(parent, matchers, onChangeWrapped);
				else if (!$isLinkNode(parent)) {
					if (textNode.isSimpleText() && (startsWithSeparator(textNode.getTextContent()) || !$isAutoLinkNode(previous))) {
						const textNodesToMatch = getTextNodesToMatch(textNode);
						handleLinkCreation(textNodesToMatch, matchers, onChangeWrapped);
					}
					handleBadNeighbors(textNode, matchers, onChangeWrapped);
				}
			}));
		};
		t1 = [
			editor,
			matchers,
			onChange
		];
		$[0] = editor;
		$[1] = matchers;
		$[2] = onChange;
		$[3] = t0;
		$[4] = t1;
	} else {
		t0 = $[3];
		t1 = $[4];
	}
	useEffect(t0, t1);
}
function AutoLinkPlugin(t0) {
	const { onChange } = t0;
	const [editor] = useLexicalComposerContext();
	useAutoLink(editor, MATCHERS, onChange);
	return null;
}

//#endregion
export { AutoLinkPlugin };
//# sourceMappingURL=AutoLinkPlugin.js.map